import posthog from 'posthog-js';
import { supabase } from '@/integrations/supabase/client';
import { isNativeShell } from '@/lib/oauthRedirect';
import { syncOnboardingFlagFromAccount } from '@/lib/onboardingFlag';
import { clearStoredReferralCode, getStoredReferralCode } from '@/lib/referral';
import { occupationLabel } from '@/lib/analytics';
import { performSignOut, clearSupabaseAuthStorage, isInvalidSessionError } from '@/lib/authCleanup';
import { filterToNullColumns, hasNoAnswers } from '@/lib/preferenceReplay';

// Engine singleton slot + test reset live in src/lib/authEngineRegistry.ts (split so test setup can reset without loading a real Supabase client).
import { getEngineSlot } from '@/lib/authEngineRegistry';


import type { User, Session } from '@supabase/supabase-js';

/**
 * Single app-wide auth engine.
 *
 * Previously `useAuth` resolved the session inside its own effect, so EVERY
 * consumer mount ran getSession() + getUser() and registered its own
 * onAuthStateChange listener (~94 production call sites). The engine resolves
 * ONCE per app load; every consumer reads the same snapshot. The listener body
 * below is the one that used to live in useAuth, moved unchanged.
 */

// Last known signed-in user id. Persisted in localStorage so it survives cold
// starts / app restarts — otherwise every launch would look like "no previous
// user". Used to decide whether a SIGNED_IN event is a real identity change
// (supabase-js re-fires SIGNED_IN on every hidden -> visible transition).
const LAST_USER_ID_KEY = 'grounded_last_user_id';

function readLastUserId(): string | null {
  try {
    return localStorage.getItem(LAST_USER_ID_KEY);
  } catch {
    return null;
  }
}

function writeLastUserId(id: string) {
  try {
    localStorage.setItem(LAST_USER_ID_KEY, id);
  } catch {
    // ignore
  }
}

function clearLastUserId() {
  try {
    localStorage.removeItem(LAST_USER_ID_KEY);
  } catch {
    // ignore
  }
}

function identifyWithProfile(user: User) {
  const platform = isNativeShell() ? 'ios' : 'web';
  supabase
    .from('user_preferences')
    .select('occupation')
    .eq('user_id', user.id)
    .maybeSingle()
    .then(({ data }) => {
      posthog.identify(user.id, {
        email: user.email,
        created_at: user.created_at,
        platform,
        user_type: occupationLabel(data?.occupation),
      });
    });
}

export interface AuthSnapshot {
  user: User | null;
  session: Session | null;
  isLoading: boolean;
}

export interface AuthEngine {
  getSnapshot: () => AuthSnapshot;
  subscribe: (listener: () => void) => () => void;
  signOut: () => Promise<void>;
}

const INITIAL_SNAPSHOT: AuthSnapshot = { user: null, session: null, isLoading: true };


/**
 * Local state that must be dropped whenever the user ends up signed out.
 *
 * Lives outside the listener because sign-out has TWO exits: the normal
 * SIGNED_OUT event, and the local-only / storage-wipe fallback inside
 * performSignOut where supabase-js never emits SIGNED_OUT. Both must clean up.
 * Every step is independently guarded — a storage failure must never block
 * sign-out (see the forced snapshot clear in signOut).
 */
function clearSignedOutLocalState() {
  try { posthog.reset(); } catch { /* ignore */ }
  try {
    const browseKeys = Object.keys(localStorage).filter(
      k => k.startsWith('grounded_browse_') &&
           k !== 'grounded_browse_location_prompted' &&
           k !== 'grounded_browse_prefs_prompted' &&
           k !== 'grounded_browse_location' &&
           k !== 'grounded_browse_distance'
    );
    browseKeys.forEach(k => localStorage.removeItem(k));
  } catch { /* ignore */ }
  clearLastUserId();
  try { localStorage.removeItem('grounded_onboarding_in_progress'); } catch { /* ignore */ }
  try { sessionStorage.removeItem('proximity-prompt-resolved'); } catch { /* ignore */ }
  try { sessionStorage.removeItem('proximity-dismissed-cafes'); } catch { /* ignore */ }
  try { sessionStorage.removeItem('onesignal-player-id'); } catch { /* ignore */ }
}

function startEngine(): AuthEngine {
  let snapshot: AuthSnapshot = INITIAL_SNAPSHOT;
  const listeners = new Set<() => void>();

  // Guards against running the sign-out cleanup twice for ONE sign-out (the
  // event path and the forced path can both fire). Reset the moment a session
  // exists again, so a SECOND sign-out in the same app load cleans up too.
  let signOutCleanupDone = false;
  const runSignOutCleanup = () => {
    if (signOutCleanupDone) return;
    signOutCleanupDone = true;
    try { clearSignedOutLocalState(); } catch { /* never block sign-out */ }
  };

  // A NEW object identity on every change is what tells useSyncExternalStore to
  // re-render. Mutating in place would silently strand every subscriber.
  const setSnapshot = (next: Partial<AuthSnapshot>) => {
    snapshot = { ...snapshot, ...next };
    if (snapshot.session || snapshot.user) signOutCleanupDone = false;
    listeners.forEach((l) => l());
  };

  // ---- Registered exactly once for the life of the document. ----
  const { data } = supabase.auth.onAuthStateChange((event, session) => {
    setSnapshot({ session, user: session?.user ?? null, isLoading: false });


    // Clear persisted browse filters on sign-out or new sign-in
    // so each session starts with a clean slate
    if (event === 'INITIAL_SESSION' && session?.user) {
      // Defer: Supabase holds the auth lock while this callback runs, and
      // identifyWithProfile issues a Supabase query that needs that same
      // lock. Calling it inline can deadlock / re-fire auth events.
      const u = session.user;
      setTimeout(() => identifyWithProfile(u), 0);
    }

    if (event === 'SIGNED_OUT') {
      runSignOutCleanup();

    } else if (event === 'SIGNED_IN' && session?.user) {
      // CRITICAL: Supabase holds a global auth lock for the duration of this
      // callback. Every Supabase call below internally calls getSession() to
      // attach the auth header, which needs that SAME lock — calling them
      // inline re-enters the lock and churns it badly enough to RE-FIRE
      // SIGNED_IN in a loop. Deferring to a macrotask lets the callback return
      // and release the lock first. See
      // https://supabase.com/docs/reference/javascript/auth-onauthstatechange
      const u = session.user;
      setTimeout(() => {
        identifyWithProfile(u);

        // Referral tagging safety net — runs on EVERY sign-in (not just brand-new).
        // The DB write only succeeds when profile.referral_code is still null
        // (`.is('referral_code', null)`), so it's a no-op for already-tagged users.
        // This covers OAuth signups (where trigger can't see options.data) AND
        // email/password cases where the trigger ran before metadata arrived.
        const referralCode = getStoredReferralCode();
        if (referralCode) {
          supabase
            .from('profiles')
            .update({ referral_code: referralCode, referred_at: new Date().toISOString() })
            .eq('id', u.id)
            .is('referral_code', null)
            .select('id')
            .then(({ error }) => {
              // Only clear localStorage when the update actually wrote a row
              // (or the row already had a code). Don't clear on network errors.
              if (!error) {
                clearStoredReferralCode();
              }
            });
        }

        // First-time signup detection: created_at and last_sign_in_at within 5s
        // means this is a brand-new account (covers OAuth first sign-up).
        // Sets grounded_just_signed_up immediately so TermsAcceptanceDialog and
        // LocationPromptDialog see it right after sign in; the claim gate below
        // sets it only after two network round trips. signup_platform is written
        // ONLY by the claim gate (isNativeShell based), never here.
        try {
          const created = new Date(u.created_at).getTime();
          const lastSignIn = new Date(u.last_sign_in_at ?? u.created_at).getTime();
          if (Math.abs(lastSignIn - created) < 5000) {
            localStorage.setItem('grounded_just_signed_up', '1');
          }
        } catch {
          // ignore
        }

        // Wipe persisted browse filters ONLY on a real identity change.
        // supabase-js re-fires SIGNED_IN on every hidden -> visible
        // transition (tab switch, iOS shell foregrounding); wiping there
        // erased the user's filters during routine backgrounding.
        //   same id      -> no wipe
        //   different id -> wipe, then store the new id
        //   no stored id -> store the id, no wipe (guest filters survive)
        const lastUserId = readLastUserId();
        if (lastUserId && lastUserId !== u.id) {
          const browseKeys = Object.keys(localStorage).filter(
            k => k.startsWith('grounded_browse_') &&
                 k !== 'grounded_browse_location_prompted' &&
                 k !== 'grounded_browse_prefs_prompted' &&
                 k !== 'grounded_browse_location' &&
                 k !== 'grounded_browse_distance'
          );
          browseKeys.forEach(k => localStorage.removeItem(k));
        }
        if (lastUserId !== u.id) writeLastUserId(u.id);

        // Sign up vs login telemetry, exactly once per account. The database
        // is the gate: profiles.signup_completed_at stays null until the sign
        // up event has been recorded, so a reinstall, a second device, or a
        // delayed email confirmation can never double-fire sign_up_completed,
        // and a returning sign-in is never miscounted as a sign up.
        if (lastUserId !== u.id) {
          const platform = isNativeShell() ? 'ios' : 'web';
          const method = (u.app_metadata?.provider as string | undefined) ?? 'email';
          // Claim: the conditional update writes a row only for the first
          // sign in that ever reaches here, so only that one fires sign up.
          supabase
            .from('profiles')
            .update({ signup_completed_at: new Date().toISOString(), signup_platform: platform })
            .eq('id', u.id)
            .is('signup_completed_at', null)
            .select('id')
            .then(({ data, error }) => {
              // Unknown state: fire nothing rather than risk a double-fire.
              if (error) return;
              const claimed = Array.isArray(data) ? data.length > 0 : !!data;
              if (claimed) {
                try { localStorage.setItem('grounded_just_signed_up', '1'); } catch { /* ignore */ }
                try { posthog.capture('sign_up_completed', { method, platform }); } catch { /* ignore */ }
              } else {
                try { posthog.capture('logged_in', { method, platform }); } catch { /* ignore */ }
              }
            });

          // Rule 3d: an account that already finished onboarding gets the
          // device flag (new phone, reinstall) so the app menu shows.
          void syncOnboardingFlagFromAccount(u.id);
        }

        // Migrate guest browse preferences to database. This replays a CACHED
        // answer, so it fills null columns only — a value already stored is
        // newer than the cache and must survive (see lib/preferenceReplay).
        const pendingPrefs = localStorage.getItem('grounded_pending_browse_prefs');
        if (pendingPrefs) {
          (async () => {
            // A corrupt cached value can never succeed on retry — clear it.
            let prefs: Record<string, unknown>;
            try {
              prefs = JSON.parse(pendingPrefs);
            } catch {
              localStorage.removeItem('grounded_pending_browse_prefs');
              return;
            }
            try {
              const columns = [
                'wifi_importance',
                'outlet_importance',
                'seating_comfort_importance',
                'parking_importance',
              ] as const;
              const { data: existing, error: readError } = await supabase
                .from('user_preferences')
                .select(columns.join(','))
                .eq('user_id', u.id)
                .maybeSingle();
              // A failed read is not "no row exists" — keep the cache and
              // retry on the next sign-in.
              if (readError) return;

              const payload = filterToNullColumns(
                {
                  user_id: u.id,
                  wifi_importance: prefs.wifi_importance,
                  outlet_importance: prefs.outlet_importance,
                  seating_comfort_importance: prefs.seating_comfort_importance,
                  parking_importance: prefs.parking_importance,
                },
                existing as unknown as Record<string, unknown> | null,
              );

              if (!hasNoAnswers(payload)) {
                const { error } = await supabase
                  .from('user_preferences')
                  // eslint-disable-next-line @typescript-eslint/no-explicit-any
                  .upsert(payload as any, { onConflict: 'user_id' });
                // Keep the cache on failure so the next sign-in retries.
                if (error) return;
              }
              localStorage.removeItem('grounded_pending_browse_prefs');
            } catch {
              // Keep the cache so the next sign-in retries.
            }
          })();
        }

        // NOTE: the "save this list" replay intentionally lives ONLY in
        // SharedList's landing effect. save_shared_list is not idempotent, so
        // a second caller here could create duplicate collections.
      }, 0);
    }
  });
  const subscription = data?.subscription ?? null;
  getEngineSlot().teardown = () => subscription?.unsubscribe();

  // ---- Initial resolution. Runs once per app load. ----
  supabase.auth.getSession().then(({ data: { session } }) => {
    setSnapshot({ session, user: session?.user ?? null, isLoading: false });

    // Background validity check: getSession() only reads the persisted token
    // from storage, so an expired/revoked session still "loads" — that's the
    // exact state that later blank-screens on the first authed call (e.g.
    // add-cafe → 401). Validate against the server and, if the token is dead,
    // clear it so onAuthStateChange fires SIGNED_OUT and the UI drops to a
    // clean logged-out state instead of trapping the user.
    //
    // This is the only browser-side getUser() on the auth path, and it now runs
    // once per app load rather than once per consumer mount.
    if (session) {
      supabase.auth.getUser().then(({ error }) => {
        if (!error) return;
        // Only clear on a genuine auth rejection (expired/revoked token). A
        // network failure also returns an error here; signing out on that
        // would spuriously log out users who open the app offline.
        if (!isInvalidSessionError(error)) {
          console.warn('[useAuth] session validation skipped (non-auth error):', error.message);
          return;
        }
        console.warn('[useAuth] stored session invalid, clearing:', error.message);
        clearSupabaseAuthStorage();
        supabase.auth.signOut({ scope: 'local' }).catch(() => {
          // ignore — storage already cleared above
        });
      });
    }
  });

  const signOut = async () => {
    // Snapshot user id before sign-out clears state.
    const userId = snapshot.user?.id;

    // All remote cleanups are best-effort and time-boxed; the local sign-out is
    // guaranteed (with a direct storage wipe fallback). This means an already
    // expired/revoked session can no longer trap the user — sign-out always
    // completes. See performSignOut for the ordering and invariants.
    await performSignOut({
      userId,
      invokeCancelPushes: () => supabase.functions.invoke('cancel-scheduled-pushes'),
      unbindPushDevice: async () => {
        const despia = (window as unknown as { despia?: (url: string) => Promise<unknown> }).despia;
        if (typeof despia === 'function') {
          return despia('removeexternaluserid://');
        }
      },
      signOutGlobal: () => supabase.auth.signOut(),
      signOutLocal: () => supabase.auth.signOut({ scope: 'local' }),
      clearStorage: () => clearSupabaseAuthStorage(),
    });

    // Same local cleanup the SIGNED_OUT branch runs. Needed because that event
    // never arrives on the local-only / storage-wipe fallback path, which would
    // otherwise leave grounded_browse_* and grounded_last_user_id behind while
    // the UI shows signed out. Idempotent via signOutCleanupDone, and fully
    // swallowed so a storage failure cannot block the snapshot clear below.
    runSignOutCleanup();

    // Guarantee every subscriber observes the signed-out state even when the
    // SIGNED_OUT event never arrives, and even if the cleanup above threw.
    if (snapshot.user || snapshot.session) {
      setSnapshot({ user: null, session: null, isLoading: false });
    }

  };

  return {
    getSnapshot: () => snapshot,
    subscribe: (listener: () => void) => {
      listeners.add(listener);
      // Consumers come and go; the engine itself is never torn down.
      return () => { listeners.delete(listener); };
    },
    signOut,
  };
}

export function ensureAuthEngine(): AuthEngine {
  const slot = getEngineSlot();
  if (!slot.engine) slot.engine = startEngine();
  return slot.engine as AuthEngine;
}

export { __resetAuthEngineForTests } from '@/lib/authEngineRegistry';
